import { Barcode } from "lucide-react";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "../../../components/ui/toggle-group/toggle-group";
import { useMTUs } from "../../../queries/mtu/use-mtus";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form/form";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Skeleton } from "../../../components/ui/skeleton/skeleton";
import { QueryError } from "@/components/ui/query-error/query-error";
import { useEffect } from "react";
import { Switch } from "@/components/ui/switch/switch";

const mtuSchema = (t: TFunction<"translation">) =>
  z.object({
    mtuCodes: z.array(z.string()).min(1, t("nc.mtu.error")),
    showAll: z.boolean(),
  });

export const useMTUForm = () => {
  const { t } = useTranslation();
  return useForm<z.infer<ReturnType<typeof mtuSchema>>>({
    resolver: zodResolver(mtuSchema(t)),
    defaultValues: {
      mtuCodes: [],
      showAll: false,
    },
  });
};

export function SelectMTU({ form }: { form: ReturnType<typeof useMTUForm> }) {
  const showAll = form.watch("showAll");
  const { selected, MTUs, isPending, error, refetch } = useMTUs(showAll);
  const selectedMtu =
    selected?.DM_MaterialTrackingUnit.MaterialTrackingUnit.Code;
  const { t } = useTranslation();

  useEffect(() => {
    const values = form.getValues("mtuCodes");
    if (values.length || !selectedMtu) return;

    form.clearErrors("mtuCodes");
    form.setValue("mtuCodes", [selectedMtu]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMtu]);

  if (isPending) {
    return (
      <div className="flex flex-row flex-wrap  gap-2.5 gap-y-1.5">
        {[...Array(10)].map((_, i) => (
          <Skeleton className="h-[50px] basis-[calc(50%-8px)]" key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return <QueryError resource={t("nc.mtu.mtu")} retries={[refetch]} />;
  }

  return (
    <Form {...form}>
      <FormField
        control={form.control}
        name="showAll"
        render={({ field }) => (
          <label className="flex gap-4 mb-4 pr-2 justify-end items-center cursor-pointer">
            <p className="text-primary">{t("nc.mtu.showAll")}</p>
            <Switch checked={field.value} onCheckedChange={field.onChange} />
          </label>
        )}
      />
      <FormField
        control={form.control}
        name="mtuCodes"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <ToggleGroup
                type="multiple"
                onValueChange={field.onChange}
                value={field.value}
                className="grid sm:grid-cols-2"
              >
                {MTUs?.map((mtu) => (
                  <ToggleGroupItem
                    className="text-base"
                    key={mtu.Id}
                    value={
                      mtu.DM_MaterialTrackingUnit.MaterialTrackingUnit.Code ??
                      mtu.Id
                    }
                  >
                    <Barcode className="h-6 w-6" />
                    {mtu.DM_MaterialTrackingUnit.MaterialTrackingUnit.Code}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </Form>
  );
}
